<template>
  <div>
    <my-loader :loader="loader"></my-loader>
    <club-player-tabs />

    <!-- Фильтры -->
    <v-card class="filters mb-4">
      <v-row>
        <v-col
          cols="12"
          sm="6"
          md="3"
        >
          <v-select
            v-model="selectedStatus"
            :items="statusOptions"
            label="Фильтр по статусу"
            clearable
            dense
            outlined
            hide-details
            :disabled="loading"
            @change="applyFilters"
          ></v-select>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          md="3"
        >
          <v-menu
            v-model="dateMenu"
            attach
            offset-y
            :close-on-content-click="false"
            content-class="club-player-date-menu"
          >
            <template #activator="{ on, attrs }">
              <v-text-field
                v-model="dateFilter"
                label="Фильтр по дате"
                readonly
                clearable
                dense
                outlined
                hide-details
                :disabled="loading"
                v-bind="attrs"
                v-on="on"
                @click:clear="onDateFilterClear"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="dateFilter"
              @input="onDatePicked"
            ></v-date-picker>
          </v-menu>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          md="3"
        >
          <v-text-field
            v-model="userNameFilter"
            label="Фильтр по пользователю"
            clearable
            dense
            outlined
            hide-details
            :disabled="loading"
            @keydown.enter="applyFilters"
            @click:clear="onUserNameFilterClear"
          ></v-text-field>
        </v-col>
        <v-col
          cols="12"
          sm="6"
          md="3"
        >
          <v-text-field
            v-model="giftTitleFilter"
            label="Фильтр по подарку"
            clearable
            dense
            outlined
            hide-details
            :disabled="loading"
            @keydown.enter="applyFilters"
            @click:clear="onGiftTitleFilterClear"
          ></v-text-field>
        </v-col>
      </v-row>
    </v-card>

    <!-- Таблица -->
    <v-card>
      <v-data-table
        mobile-breakpoint="100"
        :headers="headers"
        :items="gift_list"
        item-key="request_pk"
        :loading="loading"
        :items-per-page="-1"
        :hide-default-footer="true"
        :no-data-text="emptyTableText"
        class="elevation-1"
        loading-text="Загрузка данных"
      >
        <template #[`item.request_pk`]="{ item }">
          <div class="d-flex flex-column">
            <span class="d-block font-weight-semibold">{{ item.request_pk }}</span>
          </div>
        </template>

        <template #[`item.action`]="{ item }">
          <div
            v-if="item.request_status != 'COMPLETED'"
            class="d-flex flex-nowrap align-center"
          >
            <v-btn
              small
              color="primary"
              :disabled="loading"
              @click="completeGiftRequest(item.request_pk)"
            >
              Выдать
            </v-btn>
            <v-btn
              small
              color="error"
              class="ml-2"
              :disabled="loading"
              @click="deleteGiftRequest(item.request_pk)"
            >
              Удалить
            </v-btn>
          </div>

          <span v-if="item.request_status == 'COMPLETED'"> - </span>
        </template>

        <template #[`item.request_status`]="{ item }">
          <v-chip
            class="font-weight-medium"
            :color="statusColor[item.request_status]"
            small
          >
            {{ status[item.request_status] }}
          </v-chip>
        </template>
      </v-data-table>

      <div
        v-if="total_page > 1"
        class="d-flex justify-center py-4"
      >
        <v-pagination
          v-model="current_page"
          :length="total_page"
          :total-visible="7"
          :disabled="loading"
          @input="giftRequests"
        />
      </div>
    </v-card>
  </div>
</template>

<script>
import Api from '@/api/api'
import MyLoader from '@/components/MyLoader.vue'
import ClubPlayerTabs from '@/views/club_player/ClubPlayerTabs.vue'

export default {
  components: { MyLoader, ClubPlayerTabs },
  data() {
    return {
      loading: false,
      loader: false,
      statusColor: {
        CREATED: 'info',
        PROCESSING: 'danger',
        COMPLETED: 'success',
      },
      errorMessages: '',
      current_page: 1,
      total_page: null,
      pageSize: 50,
      gift_list: [],
      headers: [
        {
          text: 'ID',
          value: 'request_pk',
          align: 'start',
          width: '100',
        },
        { text: 'Подарок', value: 'gift_title' },
        { text: 'Пользователь', value: 'user_name' },
        { text: 'Дата', value: 'date' },
        { text: 'Статус', value: 'request_status' },
        { text: 'Действие', value: 'action', width: '200' },
      ],
      status: {
        CREATED: 'создан',
        PROCESSING: 'в обработке',
        COMPLETED: 'выдан',
      },
      selectedStatus: null,
      dateFilter: null,
      dateMenu: false,
      userNameFilter: '',
      giftTitleFilter: '',
      statusOptions: [
        { text: 'Создан', value: 'CREATED' },
        { text: 'Выдан', value: 'COMPLETED' },
      ],
    }
  },
  computed: {
    hasActiveFilters() {
      return !!(
        this.selectedStatus
        || this.dateFilter
        || (this.userNameFilter && this.userNameFilter.trim())
        || (this.giftTitleFilter && this.giftTitleFilter.trim())
      )
    },
    emptyTableText() {
      return this.hasActiveFilters
        ? 'По выбранным фильтрам ничего не найдено'
        : 'Заявки не найдены'
    },
  },
  mounted() {
    this.giftRequests()
  },
  methods: {
    // Query params: page, request_status, created_at_*, user_name, gift_title
    onDateFilterClear() {
      this.dateFilter = null
      this.dateMenu = false
      this.applyFilters()
    },
    onUserNameFilterClear() {
      this.userNameFilter = ''
      this.applyFilters()
    },
    onGiftTitleFilterClear() {
      this.giftTitleFilter = ''
      this.applyFilters()
    },
    onDatePicked(date) {
      this.dateFilter = date
      this.dateMenu = false
      this.applyFilters()
    },
    buildGiftRequestParams() {
      const params = {
        page: this.current_page,
        page_size: this.pageSize,
      }
      if (this.selectedStatus) {
        params.request_status = this.selectedStatus
      }
      if (this.dateFilter) {
        params.created_at_from = this.dateFilter
        params.created_at_to = this.dateFilter
      }
      if (this.userNameFilter && this.userNameFilter.trim()) {
        params.user_name = this.userNameFilter.trim()
      }
      if (this.giftTitleFilter && this.giftTitleFilter.trim()) {
        params.gift_title = this.giftTitleFilter.trim()
      }
      return params
    },
    applyFilters() {
      this.current_page = 1
      this.giftRequests()
    },
    giftRequests() {
      this.loading = true
      this.errorMessages = ''
      Api.giftRequests({ params: this.buildGiftRequestParams() })
        .then(event => {
          this.$set(this, 'gift_list', event.results || [])
          this.$set(this, 'current_page', event.current_page || this.current_page)
          this.$set(this, 'total_page', event.num_pages || 0)
        })
        .catch(error => {
          console.log(error)
          this.$set(this, 'errorMessages', 'Не удалось загрузить данные')
          this.$set(this, 'gift_list', [])
          this.$set(this, 'total_page', 0)
        })
        .finally(() => {
          this.$set(this, 'loading', false)
        })
    },
    completeGiftRequest(gift_request_pk) {
      if (!confirm('Вы уверены что хотите выдать подарок?')) {
        return
      }
      this.loading = true
      Api.completeGiftRequest({ gift_request_pk })
        .then(() => {
          this.giftRequests()
        })
        .catch(error => {
          console.log(error)
          this.loading = false
        })
    },
    deleteGiftRequest(gift_request_pk) {
      if (!confirm('Вы уверены что хотите удалить запрос на получение подарка?')) {
        return
      }
      this.loading = true
      Api.deleteGiftRequest({ gift_request_pk })
        .then(() => {
          this.giftRequests()
        })
        .catch(error => {
          console.log(error)
          this.loading = false
        })
    },
  },
}
</script>

<style>
.filters {
  padding: 10px;
  background-color: #e6e6eb;
  border-radius: 5px;
  margin-bottom: 20px;
  overflow: visible;
}

.club-player-date-menu {
  z-index: 8;
}

.href {
  text-decoration: underline;
  color: #2f65ae;
  cursor: pointer;
}

.label {
  font-size: 13px;
}

.search_calendar {
  margin-bottom: 20px;
  margin-top: 20px;
}

.mx-input {
  height: 48px;
}

.more_small {
}
</style>
